<template>
  <div class="role-card">
    <div class="role-card__header">
      <div class="role-card__title">
        <div class="role-card__title-indicator" />
        {{ currentRole.name }}
      </div>
      <Select
        :iconsLargeOnRight="false"
        :items="roleOptions"
        :labelIcons="roleIcons"
        :modelValue="currentRoleState.role"
        @update:modelValue="updateRole"
      />
      <Dropdown
        v-if="currentRoleState.role !== 'no_access'"
        :items="tempAccessLabel"
        placement="left-start"
        @click-item="grantTempAccess"
      >
        <UseSvg
          class="icon role-card__trigger"
          name="vertical_ellipsis"
        />
      </Dropdown>
    </div>
    <div
      v-if="currentRoleState.temporary_access"
      class="role-card__access"
    >
      <div class="role-card__access-expiry">
        <Clock12ExpirySVG />
        <div class="typo-small-title">
          {{ $t('user_operations.access_expires_in') }}
        </div>
      </div>
      <div class="date-picker">
        <DatePicker
          v-model="date"
          :minDate="new Date()"
          @update:modelValue="updateTempAccessDate"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { isoDate } from '@/functions/date_format_helpers';
import { useRoute } from 'vue-router';
import I18n from 'i18n-js';

import UseSvg from '@/components/generic/use_svg.vue';
import Dropdown from '@/components/generic/dropdown.vue';
import Select from '@/components/form/select.vue';
import DatePicker from '@/components/form/date_picker.vue';

import Clock12ExpirySVG from '@/images/clock-12-expiry.svg';

const props = defineProps({
  currentRole: { type: Object, required: true },
  roleTypes: { type: Array, required: true },
  operationType: { type: String, required: true },
});
const emit = defineEmits(['update']);

// eslint-disable-next-line vue/no-setup-props-destructure
const {
  id,
  role,
  temporary_access,
  temporary_access_expiry_date,
} = props.currentRole;

const route = useRoute();
const noAccessRole = props.roleTypes.find((role) => role === 'no_access');

let operationType = operationKeyValue();

const currentRoleState = ref({
  [operationType.key]: route.params.userId ? operationType.id : id,
  role: role || noAccessRole,
  temporary_access: temporary_access || false,
  temporary_access_expiry_date: temporary_access_expiry_date || null,
  id: route.params.userId ? id : null,
});

let currentDate = new Date();
currentDate.setDate(currentDate.getDate() + 1);

const date = ref(
  currentRoleState.value.temporary_access_expiry_date !== null
    ? new Date(currentRoleState.value.temporary_access_expiry_date)
    : currentDate
);

const roleIcons = {
  operation_administrator: { name: 'wrench', class: 'icon' },
  administrator: { name: 'wrench', class: 'icon' },
  trusted_inspector: { name: 'verified_bubble', class: 'icon' },
  inspector: { name: 'hard_hat', class: 'icon' },
  planner: { name: 'checkboard', class: 'icon' },
  observer: { name: 'eye', class: 'icon' },
  no_access: { name: 'no_entry', class: 'icon' },
};

const roleOptions = computed(() =>
  props.roleTypes.map((role) => ({
    label: I18n.t(`roles.name.${role}`),
    value: role,
    details: {
      description: I18n.t(`roles.description.${role}`),
      name: roleIcons[role].name,
    },
  }))
);

const tempAccessLabel = computed(() => {
  if (!currentRoleState.value.temporary_access) {
    return [{ label: I18n.t('user_operations.grant_temporary_access') }];
  }
  return [{ label: I18n.t('user_operations.remove_temporary_access') }];
});

function operationKeyValue() {
  switch (props.operationType) {
    case 'shafts':
      return { key: 'shaft_id', id: props.currentRole.shaft_id };
    case 'shafts_v2_shafts':
      return { key: 'shafts_v2_shaft_id', id: props.currentRole.shafts_v2_shaft_id };
    case 'ships':
      return { key: 'ship_id', id: props.currentRole.ship_id };
    case 'equipment':
      return { key: 'equipment_id', id: props.currentRole.equipment_id };
    default:
      return { key: 'operation_id', id: props.currentRole.operation_id };
  }
}

/**
 * Update role
 */
function updateRole(newRole) {
  currentRoleState.value.role = newRole;

  if (newRole === 'no_access') {
    date.value = currentDate;
    currentRoleState.value.temporary_access = false;
    currentRoleState.value.temporary_access_expiry_date = null;
  }
  emit('update', currentRoleState.value, props.operationType);
}

/**
 * Grant temp access
 */
function grantTempAccess() {
  currentRoleState.value.temporary_access =!currentRoleState.value.temporary_access;

  if (currentRoleState.value.temporary_access) {
    currentRoleState.value.temporary_access_expiry_date = isoDate(date.value);
  } else {
    date.value = currentDate;
    currentRoleState.value.temporary_access_expiry_date = null;
  }

  emit('update', currentRoleState.value, props.operationType);
}

/**
 * Update temp access date
 */
function updateTempAccessDate(newDate) {
  currentRoleState.value.temporary_access_expiry_date = isoDate(newDate);

  emit('update', currentRoleState.value, props.operationType);
}
</script>

<style lang="scss" scoped>
@import '@/stylesheets/global';

.role-card {
  @include card-base;
  border: 0;
  margin: 0 auto;
  font-weight: 500;
  border-radius: map-get($border-radius, mega);

  @include breakpoint(medium) {
    margin: 0;
  }

  & + & {
    margin-top: bu(2);
  }

  &__header {
    display: flex;
    align-items: center;
    padding: bu(0.5) bu(2);
    background-color: map-get($background-color, default);
  }

  &__title {
    flex: 1;
    display: flex;
    align-items: center;
  }

  &__title-indicator {
    width: bu(1.5);
    height: bu(1.5);
    background-color: map-get($success-color, light);
    border-radius: 100%;
    margin-right: bu(2.26);
  }

  &__access {
    align-items: center;
    background-color: map-get($background-color, lightest);
    display: flex;
    justify-content: space-between;
    padding: bu(0.5) bu(2.5) bu(0.5) bu(2);
  }

  &__access-expiry {
    align-items: center;
    display: flex;

    svg {
      margin-right: bu(2.26);
    }
  }

  &__trigger {
    border-left: 1px solid map-get($background-color, lighter);
    margin-left: bu(1);
  }
}
</style>
